import { logger } from '../logger-client.js'
import {
  ensureSessionCapability,
  SESSION_HEADER,
} from '../../core/session/session-identity.js'

type VisualToolName = 'screen' | 'webcam'

type VisualToolArgs = {
  focus_prompt?: string
  summaryOnly?: boolean
  sessionId?: string
}

type VisualToolResult = {
  success: boolean
  analysis?: string
  confidence?: number
  error?: string
}

function normalizeVisualToolResponse(body: unknown): VisualToolResult {
  if (!body || typeof body !== 'object') {
    return { success: false, error: 'Visual analysis temporarily unavailable' }
  }

  const record = body as Record<string, unknown>
  const output = record.output && typeof record.output === 'object'
    ? (record.output as Record<string, unknown>)
    : undefined
  const analysis =
    typeof record.analysis === 'string'
      ? record.analysis
      : typeof output?.analysis === 'string'
        ? output.analysis
        : undefined
  const confidence = typeof record.confidence === 'number' ? record.confidence : undefined
  const error = typeof record.error === 'string' ? record.error : undefined

  return {
    success: record.success === true,
    ...(analysis ? { analysis } : {}),
    ...(confidence !== undefined ? { confidence } : {}),
    ...(error ? { error } : {}),
  }
}

async function fetchVisualSnapshot(
  tool: VisualToolName,
  args: VisualToolArgs
): Promise<VisualToolResult> {
  const { sessionId, focus_prompt: focusPrompt, summaryOnly } = args

  if (!sessionId) {
    return { success: false, error: 'Session ID required' }
  }

  try {
    await ensureSessionCapability(sessionId)
    const response = await fetch(`/api/tools?tool=${tool}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        [SESSION_HEADER]: sessionId,
      },
      body: JSON.stringify({
        sessionId,
        ...(focusPrompt ? { focusPrompt, focus_prompt: focusPrompt } : {}),
        ...(summaryOnly !== undefined ? { summaryOnly } : {}),
      }),
    })

    const body = await response.json().catch(() => null)
    const result = normalizeVisualToolResponse(body)

    if (!response.ok && !result.error) {
      return { success: false, error: `Visual analysis failed (${response.status})` }
    }

    return result
  } catch (error) {
    logger.warn(`[visual-tools-client] ${tool} analysis unavailable`, {
      error: error instanceof Error ? error.message : String(error),
    })
    return { success: false, error: 'Visual analysis temporarily unavailable' }
  }
}

export function handleScreenCaptureDirect(args: VisualToolArgs): Promise<VisualToolResult> {
  return fetchVisualSnapshot('screen', args)
}

export function handleWebcamCaptureDirect(args: VisualToolArgs): Promise<VisualToolResult> {
  return fetchVisualSnapshot('webcam', args)
}
